// React imports
import React, { useEffect, useState } from "react";

// External libraries
import {
  Form,
  Input,
  Row,
  Col,
  Typography,
} from "antd";
import { FormInstance } from "antd/lib/form";

// Internal components
import { UploadImageFile } from "../../../UploadFile";

// Utils
import { FORM_LABELS, PLACEHOLDERS } from "../constants";
import { FileImageCoverProps } from "../utils";
const { TextArea } = Input;

interface TranslationFormProps {
  language: string;
  form: FormInstance;
  name: string[];
}

export const TranslationForm: React.FC<TranslationFormProps> = ({
  language,
  form,
  name
}) => {
  const [filenameCover, setFilenameCover] = useState<FileImageCoverProps>({ id: "", ext: "" });
  const [urlImageCover, setUrlImageCover] = useState<string>("");

  const messages = {
    es: {
      title: 'El título es requerido',
      prompt: 'El prompt es requerido'
    },
    en: {
      title: 'Title is required',
      prompt: 'Prompt is required'
    }
  };

  // Usamos el idioma actual para los mensajes de validación
  const currentMessages = messages[language as keyof typeof messages];

  // Observar el valor actual del cover en el formulario
  const coverValue = Form.useWatch([...name, 'cover'], form);

  // Asegura que los valores existen al cambiar de idioma
  useEffect(() => {
    const translations = form.getFieldValue('translations') || {};
    if (!translations[language]) {
      form.setFieldsValue({
        translations: {
          ...translations,
          [language]: {
            title: '',
            prompt: '',
            description: '',
            cover: ''
          }
        }
      });
    }
  }, [language, form]);

  // Inicializar filenameCover con datos existentes al editar
  useEffect(() => {
    // Solo inicializar si hay coverValue y filenameCover está completamente vacío
    if (coverValue && coverValue.length > 0 && 
        (!filenameCover?.id || filenameCover.id === "") && 
        (!filenameCover?.ext || filenameCover.ext === "")) {
      const fileNameParts = coverValue.split('.');
      if (fileNameParts.length >= 2) {
        const ext = fileNameParts.pop() || '';
        const id = fileNameParts.join('.');
        setFilenameCover({ id, ext });
      }
    }
  }, [coverValue]); // Solo depender de coverValue

  // Actualizar el valor del formulario cuando se suba un archivo
  useEffect(() => {
    if (filenameCover?.id && filenameCover?.ext) {
      const newCoverValue = `${filenameCover.id}.${filenameCover.ext}`;
      form.setFieldValue([...name, 'cover'], newCoverValue);
    }
  }, [filenameCover?.id, filenameCover?.ext, form, name]);

  return (
    <Row gutter={32}>
      <Col xs={24} md={16}>
        <Form.Item
          name={[...name, 'title']}
          rules={[
            {
              required: true,
              message: currentMessages.title,
              validateTrigger: ['onBlur', 'onChange']
            }
          ]}
          label={FORM_LABELS.title}
          validateFirst
        >
          <Input
            placeholder={PLACEHOLDERS[language as keyof typeof PLACEHOLDERS]?.title}
            size="large"
          />
        </Form.Item>
        
        <Form.Item
          name={[...name, 'description']}
          label={FORM_LABELS.description}
        >
          <Input
            placeholder={PLACEHOLDERS[language as keyof typeof PLACEHOLDERS]?.description}
            size="large"
          />
        </Form.Item>        
        
        <Form.Item
          name={[...name, 'prompt']}
          rules={[
            {
              required: true,
              message: currentMessages.prompt,
              validateTrigger: ['onBlur', 'onChange']
            }
          ]}
          label={FORM_LABELS.prompt}
          validateFirst
        >
          <TextArea
            placeholder={PLACEHOLDERS[language as keyof typeof PLACEHOLDERS]?.prompt}
            rows={8}
            style={{ resize: "vertical" }}
          />
        </Form.Item>
      </Col>
      
      <Col xs={24} md={8}>
        <Form.Item
          name={[...name, 'cover']}
          label={FORM_LABELS.cover}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        >
          <UploadImageFile
            title="Cover"
            className="uploadFile"
            typeFile="quiz"
            folder="covers"
            widthCover={300}
            heightCover={200}
            image={coverValue || ""}
            filenameCover={filenameCover}
            setfilenameCover={setFilenameCover}
            urlImageCover={urlImageCover}
            setUrlImageCover={setUrlImageCover}
          />
        </Form.Item>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {language === 'es' ? 'Formato soportado: jpeg/jpg · Peso máximo: 5 MB' : 'Supported format: jpeg/jpg · Max size: 5 MB'}
        </Typography.Text>
      </Col>
    </Row>
  );
};
